import { useState, type ReactNode } from 'react';
import { UnitfieldIcon } from '@unitfield/ui/icons';
import { unitfieldLegalUrl } from '@unitfield/ui/lib/legal';
import { initials, telHref } from './public-branding';
import { usePublicShell } from './public-brand-provider';
import styles from './shell.module.css';

/**
 * The business's logo, or a monogram tile in the brand colour when there is none or it fails
 * to load. A wide wordmark (2.2:1 or wider) already spells the name, so the name is not repeated.
 * Same rules as the continuation page's OrgMark.
 */
export function BrandMark({ onBrand = false }: { onBrand?: boolean }) {
  const { branding } = usePublicShell();
  const { name, logoUrl } = branding;
  const [logo, setLogo] = useState<{ url: string; failed: boolean; wordmark: boolean } | null>(null);
  const current = logoUrl && logo?.url === logoUrl ? logo : logoUrl ? { url: logoUrl, failed: false, wordmark: false } : null;
  const showLogo = Boolean(current && !current.failed);
  const wordmark = showLogo && current?.wordmark;

  const mark = (
    <span className={styles.mark} data-wordmark={wordmark ? '' : undefined}>
      {showLogo && current ? (
        <img
          className={styles.markLogo}
          src={current.url}
          alt={wordmark ? name : ''}
          onError={() => setLogo({ url: current.url, failed: true, wordmark: false })}
          onLoad={(event) => {
            const image = event.currentTarget;
            setLogo({ url: current.url, failed: false, wordmark: image.naturalHeight > 0 && image.naturalWidth / image.naturalHeight >= 2.2 });
          }}
        />
      ) : name ? (
        <span className={styles.monogram} aria-hidden="true">{initials(name)}</span>
      ) : null}
      {name && !wordmark ? <span className={styles.markName}>{name}</span> : null}
    </span>
  );

  // On the brand header the logo sits in a white chip so any artwork stays legible.
  return onBrand ? <span className={styles.markChip}>{mark}</span> : mark;
}

/** Phone and "Email us" pills, each only when the business has set it. */
export function ContactPills({ compact = false }: { compact?: boolean }) {
  const { branding } = usePublicShell();
  const { contactPhone, contactEmail } = branding;
  if (!contactPhone && !contactEmail) return null;
  return (
    <div className={styles.contact}>
      {contactPhone ? (
        <a className={styles.pill} href={telHref(contactPhone)}>
          <UnitfieldIcon name="telephone" size={16} aria-hidden="true" />
          {compact ? 'Call' : (
            <>
              <span className={`${styles.num} ${styles.long}`}>{contactPhone}</span>
              <span className={styles.short}>Call</span>
            </>
          )}
        </a>
      ) : null}
      {contactEmail && !(compact && contactPhone) ? (
        <a className={styles.pill} href={`mailto:${contactEmail}`} data-secondary={contactPhone ? '' : undefined}>
          <UnitfieldIcon name="mail" size={16} aria-hidden="true" />
          Email us
        </a>
      ) : null}
    </div>
  );
}

export function PublicTopBar({ trailing, onBrand = false }: { trailing?: ReactNode; onBrand?: boolean }) {
  const { branding } = usePublicShell();
  return (
    <header className={styles.topBar}>
      <BrandMark onBrand={onBrand || branding.brandHeaderBackground} />
      {trailing === undefined ? <ContactPills /> : trailing}
    </header>
  );
}

export interface PublicFooterLink {
  label: string;
  href: string;
}

/** "Powered by Unitfield" and two legal links; the default pair is the applicant's. */
export function PublicFooter({ links }: { links?: readonly [PublicFooterLink, PublicFooterLink] }) {
  const [privacy, terms] = links ?? [
    { label: 'Privacy', href: unitfieldLegalUrl('applicantPrivacy') },
    { label: 'Terms', href: unitfieldLegalUrl('rentalApplicationTerms') },
  ];
  return (
    <footer className={styles.footer}>
      <span>Powered by Unitfield</span>
      <nav aria-label="Unitfield legal">
        <a href={privacy.href} target="_blank" rel="noreferrer">{privacy.label}</a>
        <a href={terms.href} target="_blank" rel="noreferrer">{terms.label}</a>
      </nav>
    </footer>
  );
}

/**
 * The page's header block. With the business's "brand colour behind your header" option on,
 * it sits on an r28 sheet filled with the brand and its text switches to the on-brand colour.
 */
export function BrandHeader({ children, topBarTrailing }: { children: ReactNode; topBarTrailing?: ReactNode }) {
  const { branding } = usePublicShell();
  const onBrand = branding.brandHeaderBackground;
  return (
    <div className={styles.header} data-on-brand={onBrand ? '' : undefined}>
      <PublicTopBar onBrand={onBrand} trailing={topBarTrailing} />
      {children}
    </div>
  );
}
